import { useIsAuthenticated } from '@heidi/auth/state';
import { useQueryPatientProfileById } from '@heidi/patients/data';
import { useQueryDrawerLinkedSessions, useQuerySessionById } from '@heidi/session/data';
import type { ComponentType, ReactNode } from 'react';
import { patientProfileDisplayName } from '../../contracts/projects/work-composer-context-lock';

import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { MergeSessionRow } from '../projects/components/MergeSessionsApprovalDetails';
import { DeletePatientRow } from './delete-patient-profiles-approval-details';

import {
  isSessionDraftToolName,
  resolveDraftPresentation,
  SESSION_DRAFT_TOOL_NAMES,
} from '../../contracts/approval-kinds';
import {
  MULTIPLAYER_APPROVAL_TOOL_NAMES,
  readMultiplayerApprovalPreview,
} from '../../data/projects/components/ApprovalRequestBlock.multiplayer';
import type { ApprovalRequestInput } from '../projects/components/ApprovalRequestBlock.display';
import {
  MERGE_PATIENT_PROFILES_TOOL_NAME,
  MERGE_SESSIONS_TOOL_NAME,
  readMergeApprovalFields,
  readMergeSessionsApprovalFields,
} from '../projects/components/ApprovalRequestBlock.display';
import { MergePatientProfilesApprovalDetails } from '../projects/components/MergePatientProfilesApprovalDetails';
import { MergeSessionsApprovalDetails } from '../projects/components/MergeSessionsApprovalDetails';
import {
  DELETE_PATIENT_PROFILES_TOOL_NAME,
  readDeletePatientProfilesApprovalFields,
} from './delete-patient-profiles-approval';
import { DeletePatientProfilesApprovalDetails } from './delete-patient-profiles-approval-details';
import { DraftBody, type DraftApprovalBodyRenderArgs } from './draft-body';
import { LabelledContextPanel } from './labelled-context-panel';
import {
  hasPresentationDetails,
  PresentationDetails,
  presentationSubject,
} from './presentation-details';
import { readTextEditPreview } from './text-edit-preview';
import { readPresentation } from './tool-call-presentation';
import { useDraftPreview } from './use-draft-preview';

export interface ApprovalInputDetailsProps {
  approval: ApprovalRequestInput;
}

/**
 * The two rows that read other domains' data, rendered by the app: a merge row's
 * session, and a delete row's patient and linked sessions.
 */
export interface ApprovalInputDetailsHost {
  renderMergeSessionRow: (sessionId: string) => ReactNode;
  /** Keyed by the patient profile id. */
  renderDeletePatientRow: (
    patientProfileId: string,
    deleteLinkedSessions: boolean | undefined
  ) => ReactNode;
  /** A session edit's text, drawn the way the draft card draws it (TipTap). */
  renderDraftBody: (args: DraftApprovalBodyRenderArgs) => ReactNode;
}

type DetailsProps = ApprovalInputDetailsProps & ApprovalInputDetailsHost;

// `tool_input` is typed non-optional, but a malformed payload omitting it would throw.
const MergeApprovalInputDetails = ({ approval }: DetailsProps) => {
  const fields = useMemo(
    () => readMergeApprovalFields(approval.tool_input ?? {}),
    [approval.tool_input]
  );
  return <MergePatientProfilesApprovalDetails fields={fields} />;
};

const MergeSessionsApprovalInputDetails = ({
  approval,
  renderMergeSessionRow,
}: DetailsProps) => {
  const fields = useMemo(
    () => readMergeSessionsApprovalFields(approval.tool_input ?? {}),
    [approval.tool_input]
  );
  return (
    <MergeSessionsApprovalDetails fields={fields} renderSession={renderMergeSessionRow} />
  );
};

const DeletePatientProfilesApprovalInputDetails = ({
  approval,
  renderDeletePatientRow,
}: DetailsProps) => {
  const fields = useMemo(
    () => readDeletePatientProfilesApprovalFields(approval.tool_input ?? {}),
    [approval.tool_input]
  );
  return (
    <DeletePatientProfilesApprovalDetails
      fields={fields}
      renderPatient={(id) => renderDeletePatientRow(id, fields.deleteLinkedSessions)}
    />
  );
};

const ApprovalSummaryPanel = ({ summary }: { summary: string }) => {
  const intl = useIntl();
  return (
    <LabelledContextPanel
      label={intl.formatMessage({
        defaultMessage: 'What this does',
        id: 'fSetbgXpkg',
        description: 'Inline approval card: label above the plain-English action summary',
      })}
    >
      <p
        className="text-sm leading-snug text-text-secondary [overflow-wrap:anywhere]"
        data-testid="approval-input-summary"
      >
        {summary}
      </p>
    </LabelledContextPanel>
  );
};

/**
 * The text a session edit leaves, drawn as the draft card draws it, under the
 * approval's display detail. The live event's context is read too, so the text
 * doesn't change when the snapshot lands.
 */
const SessionEditApprovalInputDetails = ({ approval, renderDraftBody }: DetailsProps) => {
  const draft = resolveDraftPresentation(approval.tool_name, approval.context?.draft);
  const serverPreview =
    readTextEditPreview(approval.context, approval.tool_name, draft?.target) ??
    readTextEditPreview(approval.pendingContext, approval.tool_name, draft?.target);
  const { displayBody, tintAgainst, removesOnly } = useDraftPreview({
    toolInput: approval.tool_input ?? {},
    draft,
    serverPreview,
  });
  const detail = approval.toolCallDisplay?.detail?.trim();
  return (
    <>
      {detail ? <ApprovalSummaryPanel summary={detail} /> : null}
      {displayBody ? (
        <DraftBody
          body={displayBody}
          isStreaming={false}
          tintAgainst={tintAgainst}
          {...(removesOnly ? { removal: true } : {})}
          renderBody={renderDraftBody}
        />
      ) : null}
    </>
  );
};

const MultiplayerApprovalInputDetails = ({ approval }: DetailsProps) => {
  const intl = useIntl();
  const preview = useMemo(
    () =>
      readMultiplayerApprovalPreview(intl, approval.tool_name, approval.tool_input ?? {}),
    [approval.tool_name, approval.tool_input, intl]
  );
  return preview ? <ApprovalSummaryPanel summary={preview} /> : null;
};

/**
 * Per-tool opt-in for an approval card's body when the server sent no body
 * blocks. A tool the registry does not name never gets a dump of its input; it
 * shows only its bounded display `detail`, or nothing.
 */
const APPROVAL_INPUT_DETAILS: Record<string, ComponentType<DetailsProps>> = {
  [MERGE_PATIENT_PROFILES_TOOL_NAME]: MergeApprovalInputDetails,
  [MERGE_SESSIONS_TOOL_NAME]: MergeSessionsApprovalInputDetails,
  [DELETE_PATIENT_PROFILES_TOOL_NAME]: DeletePatientProfilesApprovalInputDetails,
  ...Object.fromEntries(
    MULTIPLAYER_APPROVAL_TOOL_NAMES.map((name) => [name, MultiplayerApprovalInputDetails])
  ),
  ...Object.fromEntries(
    SESSION_DRAFT_TOOL_NAMES.map((name) => [name, SessionEditApprovalInputDetails])
  ),
};

export const ApprovalInputDetailsView = ({
  approval,
  renderMergeSessionRow,
  renderDeletePatientRow,
  renderDraftBody,
}: DetailsProps): ReactNode => {
  const intl = useIntl();
  const { blocks } = useMemo(
    () =>
      readPresentation(approval.context ?? approval.pendingContext, approval.tool_name),
    [approval.context, approval.pendingContext, approval.tool_name]
  );
  const detail = approval.toolCallDisplay?.detail?.trim();
  // The server's blocks outrank the registry: they are read from what executes.
  if (hasPresentationDetails(blocks)) {
    return (
      <>
        {detail && isSessionDraftToolName(approval.tool_name) ? (
          <ApprovalSummaryPanel summary={detail} />
        ) : null}
        <PresentationDetails
          blocks={blocks}
          toolInput={approval.tool_input ?? {}}
          renderDraftBody={renderDraftBody}
          renderMergeSessionRow={renderMergeSessionRow}
        />
      </>
    );
  }
  const Details = APPROVAL_INPUT_DETAILS[approval.tool_name];
  if (Details) {
    return (
      <Details
        approval={approval}
        renderMergeSessionRow={renderMergeSessionRow}
        renderDeletePatientRow={renderDeletePatientRow}
        renderDraftBody={renderDraftBody}
      />
    );
  }
  // A subject the server named replaces the model-written `detail`; it heads the card.
  return detail && !presentationSubject(intl, blocks) ? (
    <ApprovalSummaryPanel summary={detail} />
  ) : null;
};

const MergeSessionApprovalRow = ({ sessionId }: { sessionId: string }) => {
  const { data } = useQuerySessionById({
    sessionId,
    select: (session) => ({
      name: session?.session_name,
      createdTimeLocal: session?.created_time_local,
    }),
  });
  return (
    <MergeSessionRow
      sessionId={sessionId}
      name={data?.name}
      createdTimeLocal={data?.createdTimeLocal}
    />
  );
};

const DeletePatientApprovalRow = ({
  patientProfileId,
  deleteLinkedSessions,
}: {
  patientProfileId: string;
  deleteLinkedSessions: boolean | undefined;
}) => {
  const { isAuthenticated } = useIsAuthenticated();
  const { data: profile } = useQueryPatientProfileById(patientProfileId, {
    isAuthenticated,
  });
  const { data, isLoading, isError } = useQueryDrawerLinkedSessions(patientProfileId);
  return (
    <DeletePatientRow
      patientProfileId={patientProfileId}
      name={patientProfileDisplayName(profile)}
      sessions={data?.sessions}
      status={isLoading ? 'loading' : isError ? 'error' : 'ready'}
      deleteLinkedSessions={deleteLinkedSessions}
    />
  );
};

const renderMergeSessionRow = (sessionId: string) => (
  <MergeSessionApprovalRow sessionId={sessionId} />
);

const renderDeletePatientRow = (
  patientProfileId: string,
  deleteLinkedSessions: boolean | undefined
) => (
  <DeletePatientApprovalRow
    key={patientProfileId}
    patientProfileId={patientProfileId}
    deleteLinkedSessions={deleteLinkedSessions}
  />
);

/** The approval card's body, bound to the session and patient reads its rows need. */
export const ApprovalInputDetails = ({
  approval,
  renderDraftBody,
}: ApprovalInputDetailsProps &
  Pick<ApprovalInputDetailsHost, 'renderDraftBody'>): ReactNode => (
  <ApprovalInputDetailsView
    approval={approval}
    renderMergeSessionRow={renderMergeSessionRow}
    renderDeletePatientRow={renderDeletePatientRow}
    renderDraftBody={renderDraftBody}
  />
);
